/**
 * Componente de filtros para a view de Processos
 * Inclui: Regional, UF e Tipo de Documento
 */
import { useProcessosFilters } from '../hooks/useProcessosFilters';
import { cn } from '@/lib/utils';
import { Filter, X, ChevronDown } from 'lucide-react';
import { REGIONALS, REGIONAL_UFS, type Regional, type BrazilUF } from '@claro/shared';

// Tipos de documento disponíveis para processos
const PROCESS_DOC_TYPES = [
  { value: 'AVCB - Auto de Vistoria do Corpo de Bombeiros', label: 'AVCB' },
  { value: 'Alvará de Funcionamento', label: 'Alvará de Funcionamento' },
  { value: 'Licença Sanitária', label: 'Licença Sanitária' },
  { value: 'Licença Ambiental', label: 'Licença Ambiental' },
  { value: 'Licença Publicidade', label: 'Licença Publicidade' },
];

interface ProcessosFiltersProps {
  className?: string;
}

export function ProcessosFilters({ className }: ProcessosFiltersProps) {
  const { regional, uf, docTypes, setRegional, setUF, setDocTypes, clearFilters } =
    useProcessosFilters();

  // UFs disponíveis baseado na regional selecionada
  const availableUFs = regional ? (REGIONAL_UFS[regional as Regional] || []) : [];

  const hasActiveFilters = (docTypes && docTypes.length > 0) || regional !== null || uf !== null;

  // Handler para select de tipo de documento
  const handleDocTypeChange = (value: string) => {
    if (value === '') {
      setDocTypes(null);
    } else {
      setDocTypes([value]);
    }
  };

  // Handler para mudança de regional
  const handleRegionalChange = (value: string) => {
    setRegional(value ? value : null);
  };

  // Handler para mudança de UF
  const handleUFChange = (value: string) => {
    setUF(value ? value : null);
  };

  // Valor atual do select de tipo
  const currentDocType = docTypes && docTypes.length > 0 ? docTypes[0] : '';

  return (
    <div className={cn('flex items-center gap-3 flex-wrap', className)}>
      {/* Ícone de filtro */}
      <div className="flex items-center gap-1.5 text-muted-foreground">
        <Filter className="w-4 h-4" />
        <span className="text-sm font-medium">Filtros:</span>
      </div>

      {/* Select de Regional */}
      <div className="relative">
        <select
          id="regional-filter-processos"
          value={regional || ''}
          onChange={(e) => handleRegionalChange(e.target.value)}
          className={cn(
            'h-9 pl-3 pr-8 text-sm rounded-md border bg-background text-foreground appearance-none',
            'focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary',
            'transition-colors',
            '[&>option]:bg-background [&>option]:text-foreground',
            regional && 'border-primary/50 bg-primary/5'
          )}
        >
          <option value="">Todas Regionais</option>
          {REGIONALS.map((reg) => (
            <option key={reg} value={reg}>
              {reg}
            </option>
          ))}
        </select>
        <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
      </div>

      {/* Select de UF (só aparece se regional selecionada) */}
      {regional && availableUFs.length > 0 && (
        <div className="relative">
          <select
            id="uf-filter-processos"
            value={uf || ''}
            onChange={(e) => handleUFChange(e.target.value)}
            className={cn(
              'h-9 pl-3 pr-8 text-sm rounded-md border bg-background text-foreground appearance-none',
              'focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary',
              'transition-colors',
              '[&>option]:bg-background [&>option]:text-foreground',
              uf && 'border-primary/50 bg-primary/5'
            )}
          >
            <option value="">Todos UFs</option>
            {availableUFs.map((u) => (
              <option key={u} value={u}>
                {u}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
        </div>
      )}

      {/* Select de Tipo de Documento */}
      <div className="relative">
        <select
          id="doc-type-filter-processos"
          value={currentDocType}
          onChange={(e) => handleDocTypeChange(e.target.value)}
          className={cn(
            'h-9 pl-3 pr-8 text-sm rounded-md border bg-background text-foreground appearance-none',
            'focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary',
            'transition-colors',
            '[&>option]:bg-background [&>option]:text-foreground',
            currentDocType && 'border-primary/50 bg-primary/5'
          )}
        >
          <option value="">Todos os Tipos</option>
          {PROCESS_DOC_TYPES.map((type) => (
            <option key={type.value} value={type.value}>
              {type.label}
            </option>
          ))}
        </select>
        <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
      </div>

      {/* Botão limpar filtros */}
      {hasActiveFilters && (
        <button
          onClick={clearFilters}
          className={cn(
            'flex items-center gap-1.5 px-3 h-9 text-sm rounded-md',
            'bg-destructive/10 text-destructive hover:bg-destructive/20',
            'transition-colors font-medium'
          )}
        >
          <X className="w-3.5 h-3.5" />
          <span>Limpar</span>
        </button>
      )}

      {/* Indicador de filtros ativos */}
      {hasActiveFilters && (
        <div className="flex items-center gap-2">
          <div className="h-5 w-px bg-border" />
          <div className="flex items-center gap-1.5 text-xs bg-muted px-2 py-1 rounded-full">
            <span className="text-muted-foreground">Filtrado:</span>
            <span className="font-medium text-foreground">
              {[regional, uf, PROCESS_DOC_TYPES.find((t) => t.value === currentDocType)?.label].filter(Boolean).join(' → ')}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
